// used for race entries and results lists
import {
  Text,
  Flex,
  Tag,
  Heading,
  Skeleton,
  FlexProps,
  Button,
  GridItem,
  Grid,
  Box,
} from '@chakra-ui/react';
import { RaceParticipants } from 'api/generated/graphql';
import {
  RCarGridCard,
  RBaseCardTopLeft,
  RTooltip,
  RTiers,
  RBaseCardTopRight,
  RBaseCardBottomRight,
  RBaseCardBottomLeft,
} from 'components';
import { AppliedLivery, Livery, RarityTier, Tyres } from 'types';
import {
  IconEmptySet,
  IconEyeHidden,
  IconEyeShow,
  IconTyreC1,
  IconTyreC2,
  IconTyreC3,
  IconTyreC4,
  IconTyreC5,
  IconTyreInter,
  IconTyreWet,
  IconWatch,
} from 'icons';
import Flags, { FlagsProps } from 'components/flags';
import { AvatarPicture } from 'components/Avatar/AvatarPicture';
import { useResults } from 'context';
import { BaseCardProps } from 'components/CarCard/CarBaseCard';
import { rarityLevels } from 'utils/themeConstants';
import RaceLevel from 'components/Races2/RaceLevel/RaceLevel';
import GridCardHeader from 'components/GridCardHeader/GridCardHeader';
import { useUserSettings } from 'hooks';

const RaceGrid = ({
  playersParticipants,
  text,
  gridType,
  carCardProps,
}: {
  playersParticipants: RaceParticipants[];
  text: string;
  gridType: 'entries' | 'results';
  carCardProps?: BaseCardProps & FlexProps;
}) => {
  const { showAllResults, setShowAllResults } = useResults();
  const { getUserSettings } = useUserSettings();
  const filtered = playersParticipants?.filter((playersParticipant) => {
    return playersParticipant?.status !== 'rejected'; // possible values are processing, accepted, rejected
  });


  const sorted =
    gridType === 'entries'
      ? filtered.sort((a, b) => {
        if (a?.qualifyingGridPosition && b?.qualifyingGridPosition) {
          return a?.qualifyingGridPosition - b?.qualifyingGridPosition; // Sort by qualifying grid position for entries
        }
        return 0;
      })
      : filtered.sort((a, b) => {
        if (a?.position && b?.position) {
          return a.position - b.position; // Sort by position for results
        }
        return 0;
      })
  const tyreIcons: Record<Tyres, JSX.Element> = {
    C1: <IconTyreC1 h="2rem" w="auto" />,
    C2: <IconTyreC2 h="2rem" w="auto" />,
    C3: <IconTyreC3 h="2rem" w="auto" />,
    C4: <IconTyreC4 h="2rem" w="auto" />,
    C5: <IconTyreC5 h="2rem" w="auto" />,
    INTER: <IconTyreInter h="2rem" w="auto" />,
    WET: <IconTyreWet h="2rem" w="auto" />,
  };
  return (
    <Flex flexDirection="column">
      {gridType === 'results' && (
        <Button
          variant="secondary"
          background="linear-gradient(225deg, rgba(255, 53, 57, 0.2) 0%, rgba(216, 56, 50, 0.2) 18%, rgba(216, 56, 50, 0.2) 73%, rgba(173, 53, 41, 0.2) 100%);"
          w="full"
          mb="6"
          alignSelf="center"
          border="1.5px solid rgba(216, 56, 50, 1)"
          color="bloodMoon.100"
          fontWeight="600"
          onClick={() => setShowAllResults((oldState) => !oldState)}
        >
          {showAllResults ? 'Hide' : 'Show'} Results{' '}
          {showAllResults ? (
            <IconEyeShow ml="0.5rem" __css={{ path: { fill: 'bloodMoon.100' } }} />
          ) : (
            <IconEyeHidden ml="0.5rem" __css={{ path: { fill: 'bloodMoon.100' } }} />
          )}
        </Button>
      )}
      {!showAllResults && sorted.length === 0 && (
        <Flex
          width="full"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <IconEmptySet mb="1rem" />
          <Heading as="h2" size="md" textTransform="uppercase">
            No Entries Yet
          </Heading>
        </Flex>
      )}
      
      <Flex pb="20" flexDirection="column">
        <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={12}>
          {sorted.map((playersParticipant, index) => {
            const { car } = playersParticipant;
            const isEvenColumn = index % 2 === 1;
            
            return (
              <GridItem 
                key={index} 
                w="full"
                h="200px"
              >
                <Flex
                  flexDir="column"
                  transform={{ md: isEvenColumn ? 'translateY(60px)' : 'none' }}
                >
                  {(playersParticipant?.qualifyingGridPosition || 
                    playersParticipant?.position || 
                    playersParticipant.user.id === getUserSettings?.data?.getUser.id) && (
                    <GridCardHeader
                      gridPosition={gridType === 'entries' ? playersParticipant?.qualifyingGridPosition : playersParticipant?.position}
                      ownCar={playersParticipant.user.id === getUserSettings?.data?.getUser.id}
                      gridType={gridType}
                      showAllResults={showAllResults}
                    />
                  )}
                  
                  <Flex
                    px={{ base: '3', md: '4' }}
                  >
                    <RCarGridCard
                      key={index}
                      isResults={gridType === 'results'}
                      backgroundPreload
                      playersParticipant={playersParticipant}
                      livery={car?.livery as Livery}
                      appliedLivery={car?.appliedLivery as AppliedLivery}
                      h="200px"
                      imageProps={{
                        transform: 'scale(70%)',
                        marginBottom: '-1rem',
                      }}
                      w="full"
                      hasCardBg
                      hasInsetBorder={false}
                      {...carCardProps}
                    >
                      <RBaseCardTopLeft>
                        <Flex
                          flexDirection="column"
                          gap="10px"
                          justifyContent="center"
                        >
                          <Skeleton isLoaded={showAllResults} mt="0.5rem">
                            <Flex alignItems="flex-start" gap="1" flexDir="column">
                              <Text 
                                fontSize={{ base: '12.5px', md: '18px' }}
                                lineHeight={{ base: '16px', md: '24px' }}
                                fontFamily="heading" 
                                fontWeight="400"
                                color="white.95"
                              >
                                {playersParticipant?.car?.name}
                              </Text>
                              <Flex alignItems="center" gap="1">
                                <Box>
                                  <RaceLevel
                                    level={rarityLevels[(playersParticipant?.car?.rarity as RarityTier) as keyof typeof rarityLevels] || rarityLevels.common}
                                    textFontSize="8px"
                                    numberFontSize="12px"
                                    minW="fit-content"
                                  />
                                </Box>
                              </Flex>
                            </Flex>
                          </Skeleton>
                        </Flex>
                      </RBaseCardTopLeft>
                      {gridType === 'results' && (
                        <RBaseCardTopRight maxW="50%" flexDir="column">
                          <Skeleton isLoaded={showAllResults}>
                            <Flex
                              flexDir="column"
                              alignItems="center"
                              flex={1}
                            >
                              <Text
                                fontSize="0.625rem"
                                lineHeight="0.75rem"
                                fontWeight={600}
                                color="white.80"
                                textTransform="uppercase"
                                textAlign="center"
                              >
                                EARNINGS
                              </Text>
                              <Flex
                                background="linear-gradient(360deg, rgba(79, 197, 186, 0.2) 0%, rgba(79, 197, 186, 0) 100%)"
                                borderRadius="4px"
                                px="3"
                                py="2"
                                borderTop="none"
                                position="relative"
                                w="80px"
                                justifyContent="center"
                                _before={{
                                  content: '""',
                                  position: "absolute",
                                  inset: 0,
                                  borderRadius: "4px",
                                  padding: "2px",
                                  paddingTop: "0px",
                                  borderWidth: "0px 1.5px 1.5px 1.5px",
                                  background: "linear-gradient(360deg, rgba(79, 197, 186, 0.48) 0%, rgba(79, 197, 186, 0) 100%)",
                                  mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                                  maskComposite: "exclude",
                                }}
                              >
                                <Text
                                  fontSize="20px"
                                  lineHeight="24px"
                                  fontWeight={600}
                                  color="treasuredTeal.100"
                                >
                                  ${(playersParticipant?.earnings || 0) / 100}
                                </Text>
                              </Flex>
                            </Flex>
                          </Skeleton>

                          <Skeleton isLoaded={showAllResults}>
                            <Flex
                              flexDir="column"
                              alignItems="center"
                              flex={1}
                            >
                              <Text
                                fontSize="0.625rem"
                                lineHeight="0.75rem"
                                fontWeight={600}
                                color="white.80"
                                textTransform="uppercase"
                                textAlign="center"
                              >
                                POINTS
                              </Text>
                              <Flex
                                background="linear-gradient(360deg, rgba(216, 56, 50, 0.2) 0%, rgba(216, 56, 50, 0) 100%)"
                                borderRadius="4px"
                                px="3"
                                py="2"
                                borderTop="none"
                                position="relative"
                                w="80px"
                                justifyContent="center"
                                _before={{
                                  content: '""',
                                  position: "absolute",
                                  inset: 0,
                                  borderRadius: "4px",
                                  padding: "2px",
                                  paddingTop: "0px",
                                  borderWidth: "0px 1.5px 1.5px 1.5px",
                                  background: "linear-gradient(360deg, rgba(216, 56, 50, 0.48) 0%, rgba(216, 56, 50, 0) 100%)",
                                  mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                                  maskComposite: "exclude",
                                }}
                              >
                                <Text
                                  fontSize="20px"
                                  lineHeight="24px"
                                  fontWeight={600}
                                  color="bloodMoon.100"
                                >
                                  {playersParticipant?.points || 0}
                                </Text>
                              </Flex>
                            </Flex>
                          </Skeleton>
                        </RBaseCardTopRight>
                      )}
                      <RBaseCardBottomRight>
                        <Flex alignItems="center" h="1.5rem" gap="2">
                          <Flags
                            countryCode={
                              showAllResults
                                ? (playersParticipant?.user
                                    ?.countryFlag as FlagsProps['countryCode']) ||
                                  'default'
                                : 'default'
                            }
                          />
                          <Skeleton
                            isLoaded={showAllResults}
                          >
                            <AvatarPicture
                              key={playersParticipant.user.avatarId}
                              avatarId={playersParticipant.user.avatarId || ''}
                            />
                          </Skeleton>
                          <Skeleton
                            isLoaded={showAllResults}
                          >
                            <Text 
                              fontSize="0.79rem"
                              lineHeight="120%"
                              fontFamily="heading" 
                              fontWeight="400"
                              color="white.95"
                              noOfLines={1}
                            >
                              {playersParticipant?.user?.playerName || 'Owner Name'}
                            </Text>
                          </Skeleton>
                        </Flex>
                      </RBaseCardBottomRight>
                      <RBaseCardBottomLeft alignItems="center">
                        <Skeleton isLoaded={gridType === 'results' ? showAllResults : true} borderRadius="full">
                          {tyreIcons[playersParticipant?.tyres.id as keyof typeof tyreIcons]}
                        </Skeleton>
                        {gridType === 'results' && (
                          <>
                            <Skeleton isLoaded={showAllResults}>
                              <Flex
                                flexDirection={{ base: 'column', md: 'row' }}
                                alignItems="center"
                                gap={4}
                                zIndex={2}
                                background="linear-gradient(225deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)"
                                borderTopRightRadius="12px"
                                borderTopLeftRadius="2px"
                                borderBottomLeftRadius="12px"
                                borderBottomRightRadius="2px"
                                px="2"
                                py="1.5"
                              >
                                <IconWatch
                                />
                              </Flex>
                            </Skeleton>
                            <Skeleton isLoaded={showAllResults}>
                              <Flex
                                flexDir="column"
                                alignItems="flex-start"
                              >
                                <Text
                                  fontSize="0.75rem"
                                  lineHeight="100%"
                                  fontFamily="heading" 
                                  fontWeight="400"
                                  color="white.80"
                                >
                                  TIME
                                </Text>
                                <Text
                                  fontSize="0.75rem"
                                  lineHeight="0.875rem"
                                  fontWeight="600"
                                  color="white.60"
                                >
                                  {playersParticipant?.time || '00:00:00'}
                                </Text>
                              </Flex>
                            </Skeleton>
                          </>
                        )}
                      </RBaseCardBottomLeft>                   
                    </RCarGridCard>
                  </Flex>
                </Flex>
              </GridItem>
            );
          })}
          {/* <GridItem w="full" h="200px">
            <RaceGridCard />
          </GridItem> */}
        </Grid>
        
      </Flex>
    </Flex>
  );
};
export default RaceGrid;